import moment, { MomentInput, Duration } from "moment";


export const formatMskEndOfDayToLocal = (inputDate: MomentInput) => {
  // 1. Parse the input date and explicitly set the time to 23:59:00 in MSK (Europe/Moscow)
  // We use moment.tz to ensure the string is interpreted in Moscow time, regardless of where the server/user is.
  const mskMoment = moment.tz(inputDate, "Europe/Moscow").set({
    hour: 23,
    minute: 59,
    second: 0,
    millisecond: 0
  });

  // 2. Convert the MSK moment to the user's local browser timezone
  const localMoment = mskMoment.clone().local();

  // 3. Format the output time (HH:mm)
  const timeString = localMoment.format('HH:mm');

  // 4. Check if the date changed across timezones (e.g., MSK to EST might push it back a day, or to JST push it forward)
  // We compare the local Date to the original input Date (ignoring time)
  const originalDateString = mskMoment.format('YYYY-MM-DD');
  const localDateString = localMoment.format('YYYY-MM-DD');

  const isDiffDate = originalDateString !== localDateString;
  const dateString = localMoment.format('DD.MM');

  // If it's still the same calendar day, just return the time
  return {
    isDiffDate,
    localDateString,
    dateString,
    timeString,
    formatted: isDiffDate
      ? `${timeString} (${dateString})`
      : timeString,
  };
}

export const endOfDayToLocal = (inputDate: MomentInput) => {
  const local = formatMskEndOfDayToLocal(inputDate);
  return {
    local: local.formatted,
    localFull: local,
    isDiffTimeZone: !local.formatted.startsWith("23:59"),
  };
}







// Returns the total duration remaining until the end of the MSK day.
export const getTimeLeft = (deadlineDateStr: string): Duration => {
    // 1. Parse the timeless string explicitly in Moscow time.
    // 2. Push it to the very end of that day in Moscow (23:59:59.999).
    const targetMsk = moment.tz(deadlineDateStr, "Europe/Moscow").endOf('day');
    
    // 3. Compare with the exact current moment (which moment evaluates in local time, 
    // but internally compares using absolute UTC timestamps, making it perfectly accurate).
    const current = moment();
    
    return moment.duration(targetMsk.diff(current));
}

// Interface for the structured response
export interface TimeLeftStructure {
    isOverdue: boolean;
    days: number;
    hours: number;
    minutes: number;
    formatted: {
        days: string;
        hours: string;
        minutes: string;
    };
}

// Converts the duration into a safe, structured object
export const parseTimeLeft = (diff: Duration): TimeLeftStructure => {
    // Check if the deadline has passed (duration is negative)
    const isOverdue = diff.asMilliseconds() < 0;

    // Use absolute values so we don't end up with negative hours/minutes in the UI
    // Math.floor(Math.abs()) safely extracts whole days without rounding up.
    const absDiff = Math.abs(diff.asMilliseconds());
    const safeDiff = moment.duration(absDiff);

    const days = Math.floor(safeDiff.asDays());
    const hours = safeDiff.hours();
    const minutes = safeDiff.minutes();

    return {
        isOverdue,
        days,
        hours,
        minutes,
        // Pre-pad strings for easy UI rendering (e.g., "05" instead of "5")
        formatted: {
            days: days.toString().padStart(2, "0"),
            hours: hours.toString().padStart(2, "0"),
            minutes: minutes.toString().padStart(2, "0"),
        }
    };
}
